import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCircleInfo,
  faExclamation,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/pro-light-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useEffect } from "react";
import cx from "classnames";

interface ToastProps {
  title: string;
  message?: string;
  type?: "success" | "error" | "warning" | "info";
  duration?: number;
  onClose: () => void;
}

type ToastTypeStyles = {
  icon: IconDefinition;
  iconColor: string;
  shadow: string;
};

function Toast({
  title,
  message,
  type = "success",
  duration = 5000,
  onClose,
}: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getTypeStyles = (): ToastTypeStyles => {
    switch (type) {
      case "success":
        return {
          icon: faCheck,
          iconColor: "text-emerald-500",
          shadow:
            "shadow-[0_6px_24px_-4px_rgba(45,190,160,0.35),0_2px_8px_rgba(0,0,0,0.06)]",
        };
      case "warning":
        return {
          icon: faExclamation,
          iconColor: "text-amber-400",
          shadow:
            "shadow-[0_6px_24px_-4px_rgba(245,158,11,0.35),0_2px_8px_rgba(0,0,0,0.06)]",
        };
      case "info":
        return {
          icon: faCircleInfo,
          iconColor: "text-gray-400",
          shadow:
            "shadow-[0_6px_24px_-4px_rgba(15,23,42,0.12),0_2px_8px_rgba(0,0,0,0.06)]",
        };
      case "error":
        return {
          icon: faTriangleExclamation,
          iconColor: "text-rose-400",
          shadow:
            "shadow-[0_6px_24px_-4px_rgba(244,63,94,0.32),0_2px_8px_rgba(0,0,0,0.06)]",
        };
      default:
        return {
          icon: faCheck,
          iconColor: "text-emerald-500",
          shadow:
            "shadow-[0_6px_24px_-4px_rgba(45,190,160,0.35),0_2px_8px_rgba(0,0,0,0.06)]",
        };
    }
  };

  const styles = getTypeStyles();
  const hasMessage = Boolean(message);
  const isSingleLine = !hasMessage || !title;
  const displayText = title || message || "";

  return (
    <div
      className={cx(
        "fixed top-4 right-4 z-[150]",
        "flex items-center w-auto max-w-md min-h-[3.25rem] px-6 py-3.5",
        "bg-white",
        styles.shadow,
        "animate-slide-in-right",
        isSingleLine ? "rounded-full" : "rounded-3xl"
      )}
      role="alert"
      aria-live={type === "error" ? "assertive" : "polite"}
    >
      <div className="flex-shrink-0 mr-3">
        <FontAwesomeIcon
          icon={styles.icon}
          className={cx("h-[1.15rem] w-[1.15rem]", styles.iconColor)}
          aria-hidden="true"
        />
      </div>

      <div className="flex-1 min-w-0">
        {isSingleLine ? (
          <p className="text-[0.9375rem] font-medium text-slate-800">
            {displayText}
          </p>
        ) : (
          <>
            <p className="text-[0.9375rem] font-medium text-slate-800">
              {title}
            </p>
            <p className="mt-0.5 text-sm text-gray-500">{message}</p>
          </>
        )}
      </div>

      <div className="flex-shrink-0 ml-6">
        <button
          type="button"
          className={cx(
            "bg-transparent rounded-md inline-flex text-gray-400",
            "hover:text-gray-600 focus:outline-none",
            "focus:ring-2 focus:ring-offset-2 focus:ring-slate-300"
          )}
          onClick={onClose}
        >
          <span className="sr-only">Close</span>
          <FontAwesomeIcon
            icon={faXmark}
            className="h-4 w-4"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
}

export default Toast;
